import { type FC, type MouseEvent, type ReactNode, useCallback, useEffect, useState } from 'react'
import type { SxProps, Theme } from '@mui/material'
import { Box, Container, Divider, Fab, Link, Paper, Typography, Zoom } from '@mui/material'
import { Link as RouterLink } from 'react-router-dom'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'
import { CommandBlock } from '../components'
import {
  backIconSx,
  backLinkSx,
  containerSx,
  dividerSx,
  inlineLinkSx,
  sectionSx,
  sectionTitleSx,
  titleSx,
} from '../styles'

export const DaemonUsagePage: FC = () => {
  return (
    <Container maxWidth="md" sx={containerSx}>
      <Link component={RouterLink} to="/docs" sx={backLinkSx}>
        <ArrowBackIcon sx={backIconSx} />
        Back to Docs
      </Link>

      <Typography variant="h5" sx={titleSx}>
        Using the Daemon
      </Typography>

      <TableOfContents />

      <Typography variant="h6" id="operating-modes" sx={sectionTitleSx}>
        Operating Modes
      </Typography>

      <Typography variant="body1" color="text.secondary" sx={sectionSx}>
        The daemon has two operating modes that determine how you interact with your Claude Code
        sessions from the dashboard.
      </Typography>

      <Typography id="cmd-passive" variant="subtitle1" sx={modeSubtitleSx}>
        Passive — View Only
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={sectionSx}>
        When you start Claude Code normally (<code>claude</code>) without the daemon running and
        Claude Code wrapped in a{' '}
        <Link component={RouterLink} to="/docs/faq#why-tmux" sx={inlineLinkSx}>
          tmux session
        </Link>
        , the daemon still{' '}
        <Link component={RouterLink} to="/docs/faq#how-it-works" sx={inlineLinkSx}>
          passively captures
        </Link>{' '}
        every hook event —{' '}
        <Link component={RouterLink} to="/docs/security" sx={inlineLinkSx}>
          encrypting
        </Link>{' '}
        and delivering each payload to the dashboard in real time. Sessions appear with randomized
        names and communication is one-way: events flow out, but no commands flow back. You can
        watch, but not interact. These sessions appear with an orange border in the dashboard.
      </Typography>
      <Box sx={infoCalloutSx}>
        <InfoOutlinedIcon sx={infoCalloutIconSx} />
        <Typography variant="body2" color="text.secondary" sx={calloutTextSx}>
          Passive mode requires no additional setup beyond the initial{' '}
          <Link component={RouterLink} to="/docs/daemon-setup#setup-the-daemon" sx={inlineLinkSx}>
            configure
          </Link>{' '}
          step. As long as hooks are configured and{' '}
          <Link
            href={`#${cmdId('hooks enable / disable')}`}
            onClick={handleTocClick(cmdId('hooks enable / disable'))}
            sx={inlineLinkSx}
          >
            enabled
          </Link>
          , events reach the dashboard regardless of whether the daemon is running.
        </Typography>
      </Box>

      <Typography id="cmd-active" variant="subtitle1" sx={modeSubtitleSx}>
        Active — Full Control
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={sectionSx}>
        When the daemon is running and Claude Code sessions are started through{' '}
        <code>c4-daemon interactive</code> → <code>session start</code>, you get full bidirectional
        control. From the dashboard you can approve plans and tool uses, answer agent questions,
        reply, switch agent modes, pause, resume, and interrupt sessions — all in real time. These
        sessions appear with a green border. Sessions launched in{' '}
        <Link
          component={RouterLink}
          to="#bypass-mode"
          onClick={handleTocClick('bypass-mode')}
          sx={inlineLinkSx}
        >
          bypass mode
        </Link>{' '}
        appear with a red border — the agent runs unrestricted.
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={sectionSx}>
        This works because <code>session start</code> launches Claude Code inside a{' '}
        <Link component={RouterLink} to="/docs/faq#why-tmux" sx={inlineLinkSx}>
          tmux session
        </Link>
        . The daemon listens for commands from the dashboard and injects them as keystrokes, exactly
        as if you were typing. Session names are user-provided during creation, so they appear in
        the dashboard with meaningful labels.
      </Typography>
      <Box sx={infoCalloutSx}>
        <InfoOutlinedIcon sx={infoCalloutIconSx} />
        <Box sx={calloutTextSx}>
          <Typography variant="body2" color="text.secondary">
            Two requirements for active mode:
          </Typography>
          <Box component="ol" sx={calloutListSx}>
            <li>
              The daemon must be running — verify with <code>status</code> that the daemon is{' '}
              <span style={{ color: '#1dd69b' }}>active</span> and start with{' '}
              <code>daemon start</code> if needed.
            </li>
            <li>
              Claude Code sessions must be launched via <code>c4-daemon interactive</code> →{' '}
              <code>session start</code> — not started with plain <code>claude</code> command.
            </li>
          </Box>
        </Box>
      </Box>

      <Box sx={comparisonTableSx}>
        <Box sx={comparisonHeaderSx}>
          <Typography sx={comparisonCellSx} />
          <Typography sx={comparisonHeadCellSx}>Passive</Typography>
          <Typography sx={comparisonHeadCellSx}>Active</Typography>
        </Box>
        <ComparisonRow label="Daemon process" passive="Not running" active="Running" />
        <ComparisonRow
          label="Session start"
          passive="claude (manual)"
          active="session start (daemon)"
        />
        <ComparisonRow label="Dashboard view" passive="Events visible" active="Events visible" />
        <ComparisonRow label="Dashboard control" passive="View only" active="Full control" />
        <ComparisonRow label="Session names" passive="Randomized" active="User-provided" />
        <ComparisonRow label="Session border" passive="Orange" active="Green (red if bypass)" />
      </Box>

      <Divider sx={dividerSx} />

      <Typography variant="body1" color="text.secondary" sx={sectionSx}>
        All daemon management happens through interactive mode.
      </Typography>
      <CommandBlock code="c4-daemon interactive" />
      <TerminalBlock content={WELCOME} />
      <Typography variant="body1" color="text.secondary" sx={sectionSx}>
        Commands are case-sensitive and must be typed in full.
      </Typography>

      <Divider sx={dividerSx} />

      <Typography variant="h6" id="system" sx={sectionTitleSx}>
        System
      </Typography>

      <CommandLabel command="status" description="config, connectivity, and feature flags" />
      <TerminalBlock content={STATUS} />

      <CommandLabel
        command="configure"
        description={
          <>
            re-runs the{' '}
            <Link component={RouterLink} to="/docs/daemon-setup" sx={inlineLinkSx}>
              setup wizard
            </Link>
          </>
        }
      />
      <TerminalBlock content={CONFIGURE} />

      <CommandLabel
        command="backend switch"
        description="switch between local and remote backends"
      />
      <TerminalBlock content={BACKEND_SWITCH} />

      <CommandLabel command="purge" description="deletes daemon cache and log files" />
      <TerminalBlock content={PURGE} />

      <CommandLabel command="exit" description="shuts down interactive mode" />

      <Divider sx={dividerSx} />

      <Typography variant="h6" id="daemon-control" sx={sectionTitleSx}>
        Daemon Control
      </Typography>

      <CommandLabel command="daemon start" description="starts the daemon" />
      <CommandLabel command="daemon stop" description="stops the daemon" />
      <CommandLabel command="daemon restart" description="restarts the daemon" />
      <TerminalBlock content={DAEMON_CONTROL} />

      <Divider sx={dividerSx} />

      <Typography variant="h6" id="sessions" sx={sectionTitleSx}>
        Sessions
      </Typography>

      <CommandLabel command="session list" description="lists all Claude Code sessions" />
      <TerminalBlock content={SESSION_LIST} />

      <CommandLabel command="session start" description="creates a new Claude Code session" />
      <Box sx={infoCalloutSx}>
        <InfoOutlinedIcon sx={infoCalloutIconSx} />
        <Typography variant="body2" color="text.secondary" sx={calloutTextSx}>
          Options 1 and 2 require cached projects from <code>discovery init</code>. If no projects
          are cached, the daemon offers to scan your filesystem first. Decline to enter a path
          manually instead.
        </Typography>
      </Box>
      <TerminalBlock content={SESSION_START_SEARCH} />
      <TerminalBlock content={SESSION_START_BROWSE} />
      <TerminalBlock content={SESSION_START_MANUAL} />
      <TerminalBlock content={SESSION_START_CURRENT} />
      <Box id="bypass-mode" sx={warningCalloutSx}>
        <WarningAmberOutlinedIcon sx={warningCalloutIconSx} />
        <Typography variant="body2" color="text.secondary" sx={calloutTextSx}>
          <strong>Bypass mode</strong> starts Claude Code with{' '}
          <code>--dangerously-skip-permissions</code> — all permission checks are disabled. The
          agent can execute commands, modify files, and access resources without asking. Only
          suitable for sandboxed or offline environments during prototyping. Not recommended for
          production development — unreviewed actions can cause irreversible damage.
        </Typography>
      </Box>
      <Box sx={infoCalloutSx}>
        <InfoOutlinedIcon sx={infoCalloutIconSx} />
        <Typography variant="body2" color="text.secondary">
          Sessions start <strong>detached</strong> by default — they run in the background while you
          continue using your terminal. To see a session&apos;s live output, use{' '}
          <code>session attach</code>. Detach at any time to return the session to the background.
        </Typography>
      </Box>

      <CommandLabel command="session end" description="ends one or more sessions" />
      <TerminalBlock content={SESSION_END} />
      <TerminalBlock content={SESSION_END_MULTI} />

      <CommandLabel command="session attach" description="attaches to a detached session" />
      <TerminalBlock content={SESSION_ATTACH} />

      <CommandLabel command="session detach" description="detaches from attached sessions" />
      <TerminalBlock content={SESSION_DETACH} />

      <CommandLabel command="session rename" description="renames an existing session" />
      <TerminalBlock content={SESSION_RENAME} />

      <CommandLabel command="session resume" description="resumes a previous Claude Code session" />
      <TerminalBlock content={SESSION_RESUME} />

      <Divider sx={dividerSx} />

      <Typography variant="h6" id="features" sx={sectionTitleSx}>
        Features
      </Typography>

      <CommandLabel
        command="hooks enable / disable"
        description="toggle conversation event processing"
      />
      <TerminalBlock content={HOOKS_TOGGLE} />

      <CommandLabel command="activity enable / disable" description="toggle activity tracking" />
      <TerminalBlock content={ACTIVITY_TOGGLE} />

      <CommandLabel
        command="persistence enable / disable"
        description="toggle event persistence to database"
      />
      <TerminalBlock content={PERSISTENCE_TOGGLE} />

      <Box sx={warningCalloutSx}>
        <WarningAmberOutlinedIcon sx={warningCalloutIconSx} />
        <Typography variant="body2" color="text.secondary">
          If your employer or client has strict policies about storing company data on external
          servers, consider disabling event persistence for transient-only events. All persisted
          data is fully{' '}
          <Link component={RouterLink} to="/docs/security" sx={inlineLinkSx}>
            end-to-end encrypted
          </Link>{' '}
          and completely unreadable by us — only your machines and dashboard can decrypt it. Follow
          your organization&apos;s security guidelines regarding external data storage.
        </Typography>
      </Box>

      <Divider sx={dividerSx} />

      <Typography variant="h6" id="security" sx={sectionTitleSx}>
        Security
      </Typography>

      <CommandLabel
        command="encryption status"
        description={
          <>
            <Link component={RouterLink} to="/docs/security" sx={inlineLinkSx}>
              key ring
            </Link>{' '}
            details and active key version
          </>
        }
      />
      <TerminalBlock content={ENCRYPTION_STATUS} />

      <CommandLabel
        command="encryption import"
        description="import a key ring from dashboard export"
      />
      <TerminalBlock content={ENCRYPTION_IMPORT} />

      <CommandLabel
        command="encryption verify"
        description="verify key ring integrity and permissions"
      />
      <TerminalBlock content={ENCRYPTION_VERIFY} />

      <Divider sx={dividerSx} />

      <Typography variant="h6" id="logs" sx={sectionTitleSx}>
        Logs
      </Typography>

      <CommandLabel command="logs status" description="log stats and disk usage" />
      <TerminalBlock content={LOGS_STATUS} />

      <CommandLabel command="logs level" description="change log verbosity" />
      <TerminalBlock content={LOGS_LEVEL} />

      <CommandLabel command="logs cleanup" description="delete log files" />
      <TerminalBlock content={LOGS_CLEANUP} />

      <Divider sx={dividerSx} />

      <Typography variant="h6" id="discovery" sx={sectionTitleSx}>
        Discovery
      </Typography>

      <CommandLabel
        command="discovery init"
        description={
          <>
            scans filesystem for Git repos, caches results for <code>session start</code>
          </>
        }
      />
      <TerminalBlock content={DISCOVERY_INIT} />

      <CommandLabel
        command="discovery update"
        description="finds new repos and prunes missing ones"
      />
      <TerminalBlock content={DISCOVERY_UPDATE} />

      <CommandLabel command="discovery describe" description="shows cached projects" />
      <TerminalBlock content={DISCOVERY_DESCRIBE} />

      <Divider sx={dividerSx} />

      <Typography variant="h6" id="cache" sx={sectionTitleSx}>
        Cache
      </Typography>

      <CommandLabel command="cache cleanup" description="removes the agent cache directory" />
      <TerminalBlock content={CACHE_CLEANUP} />

      <BackToTop />
    </Container>
  )
}

const cmdId = (cmd: string): string => `cmd-${cmd.replaceAll(/[\s/]+/g, '-')}`

const TOC_SECTIONS: readonly {
  readonly id: string
  readonly label: string
  readonly commands: readonly string[]
}[] = [
  {
    id: 'system',
    label: 'System',
    commands: ['status', 'configure', 'backend switch', 'purge', 'exit'],
  },
  {
    id: 'daemon-control',
    label: 'Daemon Control',
    commands: ['daemon start', 'daemon stop', 'daemon restart'],
  },
  {
    id: 'sessions',
    label: 'Sessions',
    commands: [
      'session list',
      'session start',
      'session end',
      'session attach',
      'session detach',
      'session rename',
      'session resume',
    ],
  },
  {
    id: 'features',
    label: 'Features',
    commands: [
      'hooks enable / disable',
      'activity enable / disable',
      'persistence enable / disable',
    ],
  },
  {
    id: 'security',
    label: 'Security',
    commands: ['encryption status', 'encryption import', 'encryption verify'],
  },
  { id: 'logs', label: 'Logs', commands: ['logs status', 'logs level', 'logs cleanup'] },
  {
    id: 'discovery',
    label: 'Discovery',
    commands: ['discovery init', 'discovery update', 'discovery describe'],
  },
  { id: 'cache', label: 'Cache', commands: ['cache cleanup'] },
]

const handleTocClick = (id: string) => (e: MouseEvent) => {
  e.preventDefault()
  const element = document.getElementById(id)
  element?.scrollIntoView({ behavior: 'smooth' })
}

const TableOfContents: FC = () => (
  <Paper variant="outlined" sx={tocContainerSx}>
    <Typography variant="subtitle2" sx={tocHeadingSx}>
      Commands
    </Typography>
    <Box component="nav" aria-label="Table of contents" sx={tocNavSx}>
      {TOC_SECTIONS.map(({ id, label, commands }) => (
        <Box key={id} sx={tocSectionSx}>
          <Link href={`#${id}`} onClick={handleTocClick(id)} sx={tocSectionLinkSx}>
            {label}
          </Link>
          <Box component="ul" sx={tocCommandListSx}>
            {commands.map((cmd) => (
              <Box component="li" key={cmd} sx={tocCommandItemSx}>
                <Link
                  href={`#${cmdId(cmd)}`}
                  onClick={handleTocClick(cmdId(cmd))}
                  sx={tocCommandLinkSx}
                >
                  <code>{cmd}</code>
                </Link>
              </Box>
            ))}
          </Box>
        </Box>
      ))}
    </Box>
  </Paper>
)

const BackToTop: FC = () => {
  const [visible, setVisible] = useState(false)

  const handleScroll = useCallback(() => {
    setVisible(window.scrollY > 400)
  }, [])

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [handleScroll])

  return (
    <Zoom in={visible}>
      <Fab
        size="small"
        aria-label="Back to top"
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        sx={backToTopSx}
      >
        <KeyboardArrowUpIcon />
      </Fab>
    </Zoom>
  )
}

const CommandLabel: FC<{
  readonly command: string
  readonly description: ReactNode
}> = ({ command, description }) => (
  <Typography id={cmdId(command)} variant="body1" color="text.secondary" sx={commandLabelSx}>
    <code>{command}</code> — {description}
  </Typography>
)

const TerminalBlock: FC<{ readonly content: string }> = ({ content }) => (
  <Box component="pre" sx={terminalBlockSx}>
    {colorize(content)}
  </Box>
)

const ComparisonRow: FC<{
  readonly label: string
  readonly passive: string
  readonly active: string
}> = ({ label, passive, active }) => (
  <Box sx={comparisonRowSx}>
    <Typography sx={comparisonLabelCellSx}>{label}</Typography>
    <Typography sx={comparisonCellSx}>{passive}</Typography>
    <Typography sx={comparisonCellSx}>{active}</Typography>
  </Box>
)

const TERMINAL_COLORS = {
  green: '#1dd69b',
  cyan: '#4fc3f7',
  yellow: '#cca700',
  red: '#f44747',
} as const

const colorize = (text: string): ReactNode[] => {
  const lines = text.split('\n')
  return lines.map((line, i) => {
    const isLast = i === lines.length - 1
    const colored = colorizeLine(line)
    return isLast ? (
      colored
    ) : (
      <span key={`${i}-${line.slice(0, 20)}`}>
        {colored}
        {'\n'}
      </span>
    )
  })
}

const CYAN_BOLD_TITLES = [
  'C4 Terminal Daemon Status',
  'Reconfiguring C4 Terminal Daemon',
  'System Purge',
  'Logs Status',
  'Select Log Level',
  'Logs Cleanup',
  'Projects Discovery',
  'Projects Discovery Update',
  'Projects Discovery Status',
  'Update Summary',
] as const

const CYAN_SECTION_LABELS = [
  'Claude Code Hooks Status',
  'Backend Switching',
  'Interactive Claude Code Sessions:',
  'System Sessions:',
  'Start Interactive Claude Code Session:',
  'Available detached Claude Code sessions:',
  'Available attached Claude Code sessions:',
  'Available Claude Code sessions:',
  'Select repositories to add:',
] as const

const DIMMED_SUBSECTIONS = [
  'New Configuration',
  'Starting daemon session in detached mode...',
  '-----------------',
  'Starting purge...',
  'Cleaning cache...',
  'Cleaning logs...',
  'Removed logs directory: /Users/user/.c4/logs',
  'Import your encryption key ring exported from the C4 Dashboard',
  'Provide the absolute path to the key ring JSON file',
  'Scanning for Git repositories in: ~',
  'This may take a while on the first run...',
  "Use 'start session' to select a project.",
  'Scanning for changes in: ~',
  'Scan completed in 1.23s',
  '- Enter numbers like: 1,2,3 or 1 2 3',
  "- Type 'all' or 'a' to add everything",
  "- Type 'none' or 'n' to skip",
  '- Press Ctrl+C to cancel',
  'Last scan: 5m ago',
  'Removed 12 cache entries from: /Users/user/.c4/cache',
] as const

const DIMMED_PROMPT_HINTS = [
  'Enter search keyword:',
  "Enter number to select (or 'b' to go back):",
  '(press Enter to use default)',
  '(press Enter for default)',
  'Press Enter for default, or type y/yes or n/no',
  'Examples: 1 | 1,2,3',
  'Enter absolute path to key ring file:',
  "(or type 'back' / 'cancel' / Ctrl+C to abort)",
] as const

const includesString = (arr: readonly string[], value: string): boolean => arr.includes(value)

const lineIndent = (line: string): string => line.slice(0, line.length - line.trimStart().length)

const SYMBOL_COLORS: Record<string, string> = {
  '\u2713': TERMINAL_COLORS.green,
  '\u2192': TERMINAL_COLORS.cyan,
  '\u2190': TERMINAL_COLORS.cyan,
  '\u2717': TERMINAL_COLORS.red,
  '!': TERMINAL_COLORS.yellow,
  '*': TERMINAL_COLORS.yellow,
}

const colorizeFullLine = (line: string): ReactNode | null => {
  if (/\[y\/N]/.test(line)) {
    return <span style={{ color: TERMINAL_COLORS.yellow }}>{line}</span>
  }
  if (/\[Y\/n]/.test(line)) {
    return <span style={{ color: TERMINAL_COLORS.cyan }}>{line}</span>
  }
  if (/^\s*\.\.\.\s*$/.test(line)) {
    return <span style={{ opacity: 0.5 }}>{line}</span>
  }
  if (/^\u2500+$/.test(line)) {
    return <span style={{ opacity: 0.5 }}>{line}</span>
  }
  if (/^\s+> .+/.test(line)) {
    return <span style={{ opacity: 0.6 }}>{line}</span>
  }
  return null
}

const colorizePromptOrHeader = (line: string): ReactNode | null => {
  const trimmed = line.trimStart()
  const indent = lineIndent(line)

  if (includesString(DIMMED_PROMPT_HINTS, trimmed)) {
    return (
      <>
        {indent}
        <span style={{ opacity: 0.6 }}>{trimmed}</span>
      </>
    )
  }

  const workingDirMatch = /^(\s*)(Working directory \[)([^\]]+)(])$/.exec(line)
  if (workingDirMatch) {
    return (
      <>
        {workingDirMatch[1] ?? ''}
        {workingDirMatch[2] ?? ''}
        <span style={{ opacity: 0.5 }}>{workingDirMatch[3] ?? ''}</span>
        {workingDirMatch[4] ?? ''}
      </>
    )
  }

  if (/^\s*Removed Repositories\b/.test(line)) {
    return (
      <>
        {indent}
        <span style={{ color: TERMINAL_COLORS.red, fontWeight: 'bold' }}>{trimmed}</span>
      </>
    )
  }
  if (/^\s*Code Repositories: \d/.test(line)) {
    return <span style={{ color: TERMINAL_COLORS.green }}>{line}</span>
  }
  if (/^\s*(?:Code |New )Repositories \(/.test(line)) {
    return (
      <>
        {indent}
        <span style={{ color: TERMINAL_COLORS.green, fontWeight: 'bold' }}>{trimmed}</span>
      </>
    )
  }
  if (/^\s*Projects\s*[:(]/.test(line)) {
    return (
      <>
        {indent}
        <span style={{ color: TERMINAL_COLORS.yellow, fontWeight: 'bold' }}>{trimmed}</span>
      </>
    )
  }

  const choicePromptMatch = /^(\s*)(Enter choice \([^)]+\): )(.+)$/.exec(line)
  if (choicePromptMatch) {
    return (
      <>
        {choicePromptMatch[1] ?? ''}
        <span style={{ color: TERMINAL_COLORS.yellow }}>{choicePromptMatch[2] ?? ''}</span>
        {choicePromptMatch[3] ?? ''}
      </>
    )
  }

  if (/^\s*Enter .+:\s*$/.test(line) && !includesString(DIMMED_PROMPT_HINTS, trimmed)) {
    return (
      <>
        {indent}
        <span style={{ opacity: 0.6 }}>{trimmed}</span>
      </>
    )
  }

  if (includesString(CYAN_BOLD_TITLES, trimmed)) {
    return (
      <>
        {indent}
        <span style={{ color: TERMINAL_COLORS.cyan, fontWeight: 'bold' }}>{trimmed}</span>
      </>
    )
  }
  if (includesString(CYAN_SECTION_LABELS, trimmed)) {
    return (
      <>
        {indent}
        <span style={{ color: TERMINAL_COLORS.cyan }}>{trimmed}</span>
      </>
    )
  }
  if (includesString(DIMMED_SUBSECTIONS, trimmed)) {
    return (
      <>
        {indent}
        <span style={{ opacity: 0.6 }}>{trimmed}</span>
      </>
    )
  }

  if (/^\s*Select project:\s*$/.test(line)) {
    return (
      <>
        {indent}
        <span style={{ fontWeight: 'bold' }}>{trimmed}</span>
      </>
    )
  }

  const sectionMatch = /^(\s*)([A-Z][^:]*:)$/.exec(line)
  if (sectionMatch) {
    return (
      <>
        {sectionMatch[1] ?? ''}
        <span style={{ opacity: 0.6 }}>{sectionMatch[2] ?? ''}</span>
      </>
    )
  }

  return null
}

const colorizeStructural = (line: string): ReactNode | null => {
  const symbolMatch = /^(\s*)([✓→←✗!*])(\s.*)$/.exec(line)
  if (symbolMatch) {
    const symbol = symbolMatch[2] ?? ''
    const color = SYMBOL_COLORS[symbol] ?? TERMINAL_COLORS.yellow
    return (
      <>
        {symbolMatch[1] ?? ''}
        <span style={{ color }}>{symbol}</span>
        {colorizeWords(symbolMatch[3] ?? '')}
      </>
    )
  }

  const treeFolderMatch = /^([├└]─) (.+)$/.exec(line)
  if (treeFolderMatch) {
    return (
      <>
        <span style={{ opacity: 0.5 }}>{treeFolderMatch[1] ?? ''}</span>{' '}
        <span style={{ fontWeight: 'bold' }}>{treeFolderMatch[2] ?? ''}</span>
      </>
    )
  }

  const treeRepoMatch = /^((?:│ {2}| {3})[├└]─) (.+)$/.exec(line)
  if (treeRepoMatch) {
    return (
      <>
        <span style={{ opacity: 0.5 }}>{treeRepoMatch[1] ?? ''}</span> {treeRepoMatch[2] ?? ''}
      </>
    )
  }

  const currentLevelMatch = /^(\s*)(\d+\.)(\s+)(\S+\s+\u2190 current)(\s+\(.+\))?\s*$/.exec(line)
  if (currentLevelMatch) {
    const description = currentLevelMatch[5] ?? ''
    return (
      <>
        {currentLevelMatch[1] ?? ''}
        <span style={{ color: TERMINAL_COLORS.cyan }}>{currentLevelMatch[2] ?? ''}</span>
        {currentLevelMatch[3] ?? ''}
        <span style={{ color: TERMINAL_COLORS.cyan, fontWeight: 'bold' }}>
          {currentLevelMatch[4] ?? ''}
        </span>
        {description ? <span style={{ opacity: 0.5 }}>{description}</span> : null}
      </>
    )
  }

  const menuMatch = /^(\s*)(\d+\.)(\s.*)$/.exec(line)
  if (menuMatch) {
    return (
      <>
        {menuMatch[1] ?? ''}
        <span style={{ color: TERMINAL_COLORS.cyan }}>{menuMatch[2] ?? ''}</span>
        {colorizeWords(menuMatch[3] ?? '')}
      </>
    )
  }

  return null
}

const colorizeLabelsAndPaths = (line: string): ReactNode | null => {
  const paddedLabelMatch = /^(\s{2,})([A-Z][A-Za-z ]+:)(\s{2,}.+)$/.exec(line)
  if (paddedLabelMatch) {
    return (
      <>
        {paddedLabelMatch[1] ?? ''}
        <span style={{ color: TERMINAL_COLORS.cyan }}>{paddedLabelMatch[2] ?? ''}</span>
        {colorizeWords(paddedLabelMatch[3] ?? '')}
      </>
    )
  }

  const logLevelMatch = /^(\s+)(Log level: )(\w+)( \u2192 )(\w+)$/.exec(line)
  if (logLevelMatch) {
    return (
      <>
        {logLevelMatch[1] ?? ''}
        {logLevelMatch[2] ?? ''}
        <span style={{ color: TERMINAL_COLORS.cyan }}>{logLevelMatch[3] ?? ''}</span>
        {logLevelMatch[4] ?? ''}
        <span style={{ color: TERMINAL_COLORS.green, fontWeight: 'bold' }}>
          {logLevelMatch[5] ?? ''}
        </span>
      </>
    )
  }

  const configLabelMatch = /^(\s{2,})([A-Z][A-Za-z ]+: )(.+)$/.exec(line)
  if (configLabelMatch) {
    return (
      <>
        {configLabelMatch[1] ?? ''}
        {configLabelMatch[2] ?? ''}
        <span style={{ color: TERMINAL_COLORS.cyan }}>{configLabelMatch[3] ?? ''}</span>
      </>
    )
  }

  const pathMatch = /^(\s{4,})(~\/.*|\/\S.*)$/.exec(line)
  if (pathMatch) {
    return (
      <>
        {pathMatch[1] ?? ''}
        <span style={{ opacity: 0.5 }}>{pathMatch[2] ?? ''}</span>
      </>
    )
  }

  return null
}

const colorizeLine = (line: string): ReactNode => {
  return (
    colorizeFullLine(line) ??
    colorizePromptOrHeader(line) ??
    colorizeStructural(line) ??
    colorizeLabelsAndPaths(line) ??
    colorizeWords(line)
  )
}

interface ColorSegment {
  readonly start: number
  readonly end: number
  readonly text: string
  readonly color?: string | undefined
  readonly opacity?: number | undefined
  readonly bold?: boolean | undefined
}

const WORD_PATTERNS: readonly {
  pattern: RegExp
  color?: string
  opacity?: number
  bold?: boolean
}[] = [
  {
    pattern: /\b(connected|available|[Rr]unning|configured|loaded|enabled|info)\b(?!:)/g,
    color: TERMINAL_COLORS.green,
  },
  { pattern: /\[current]/g, color: TERMINAL_COLORS.green },
  { pattern: /\b(disabled)\b/g, color: TERMINAL_COLORS.red },
  { pattern: /← current/g, color: TERMINAL_COLORS.cyan, bold: true },
  { pattern: /(?<=All )\d+(?= keys)/g, color: TERMINAL_COLORS.cyan },
  { pattern: /\([^)]+\)/g, opacity: 0.5 },
  { pattern: /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}Z/g, opacity: 0.5 },
]

const filterOverlaps = (segments: ColorSegment[]): ColorSegment[] => {
  segments.sort((a, b) => a.start - b.start)
  const filtered: ColorSegment[] = []
  let lastEnd = 0
  for (const seg of segments) {
    if (seg.start >= lastEnd) {
      filtered.push(seg)
      lastEnd = seg.end
    }
  }
  return filtered
}

const renderSegments = (text: string, segments: readonly ColorSegment[]): ReactNode => {
  const parts: ReactNode[] = []
  let cursor = 0
  for (const seg of segments) {
    if (seg.start > cursor) {
      parts.push(text.slice(cursor, seg.start))
    }
    const style: Record<string, string | number> = {}
    if (seg.color) style.color = seg.color
    if (seg.opacity) style.opacity = seg.opacity
    if (seg.bold) style.fontWeight = 'bold'
    parts.push(
      <span key={seg.start} style={style}>
        {seg.text}
      </span>,
    )
    cursor = seg.end
  }
  if (cursor < text.length) {
    parts.push(text.slice(cursor))
  }
  return <>{parts}</>
}

const colorizeWords = (text: string): ReactNode => {
  const segments: ColorSegment[] = []
  for (const { pattern, color, opacity, bold } of WORD_PATTERNS) {
    pattern.lastIndex = 0
    let match
    while ((match = pattern.exec(text)) !== null) {
      segments.push({
        start: match.index,
        end: match.index + match[0].length,
        text: match[0],
        color,
        opacity,
        bold,
      })
    }
  }
  if (segments.length === 0) return text
  return renderSegments(text, filterOverlaps(segments))
}

const WELCOME = `Type '?' for help, or 'exit' to shutdown
>`

const STATUS = `> status

C4 Terminal Daemon Status

Configuration:
  Host Name: My MacBook Pro
  Host ID: f47ac10b-58cc-4372-a567-0e02b2c3d479
  API Key: c4-live-a1b2...y3z4
  Environment: production
  Backend URL: https://api.c4.cordona.tech

System Health:
  Internet:            connected
  Backend:             available

Runtime State:
  Version:             1.0.0
  Daemon:              Running
  Hooks events:        enabled
  Activity events:     enabled
  Event persistence:   enabled
  Log level:           info

Encryption:
  Key ring:            configured
  User email:          user@example.com
  Total keys:          1
  Active version:      v1
  Active key:          ek_abcd...wxyz

Claude Code Hooks:
  9 c4-daemon hooks configured:
       - SessionStart
       - SessionEnd
       - UserPromptSubmit
       - PreToolUse
       - PostToolUse
       - Notification
       - Stop
       - SubagentStop
       - PreCompact`

const CONFIGURE = `> configure

Reconfiguring C4 Terminal Daemon

Claude Code Hooks Status
  \u2713 9 c4-daemon hooks configured

Current Configuration:
  Host Name: My MacBook Pro
  Host ID: f47ac10b-58cc-4372-a567-0e02b2c3d479
  API Key: c4-live-a1b2...y3z4
  Environment: production
  Backend URL: https://api.c4.cordona.tech

\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500

New Configuration
(Press Ctrl+C at any step to cancel)

  ...

  \u2713 Configuration updated successfully

  \u2192 Restarting daemon to apply new configuration...

  \u2713 Daemon session stopped

Starting daemon session in detached mode...
  \u2713 Message broker connected
  \u2713 Sessions verified: 0 running, 0 resumable
  \u2713 Daemon started successfully


  \u2713 Daemon restarted with new configuration`

const BACKEND_SWITCH = `> backend switch

Backend Switching
-----------------
  API key: test
  Current backend URL: https://api.test.c4.cordona.tech (remote)

Available backends:
1. Local development (http://localhost:<port>)
2. Remote test (https://api.test.c4.cordona.tech) [current]

Select (1-2): 1

Local backend port [8085]:
(press Enter for default)
> 8085

  Validating http://localhost:8085... \u2713
  \u2713 Backend available

Switch to test backend? [Y/n] (default: yes)
Press Enter for default, or type y/yes or n/no
> yes

  \u2713 Switched to: http://localhost:8085

Restart daemon now? [Y/n] (default: yes)
Press Enter for default, or type y/yes or n/no
> n`

const PURGE = `> purge

System Purge

  ! This will delete both cache and logs
  \u2717 WARNING: Permanently delete ALL cache and logs?
  ! Type 'PURGE' exactly (ALL CAPS required) - Attempt 1/3
(or type 'back' / 'cancel' / Ctrl+C to abort)
> PURGE


Starting purge...

Cleaning cache...

Cleaning logs...
  \u2713 Logs directory cleaned up successfully
  Removed logs directory: /Users/user/.c4/logs

  \u2713 System purge complete
`

const CACHE_CLEANUP = `> cache cleanup

Clean up cache directory? [y/N] (default: no)
Press Enter for default, or type y/yes or n/no
> y
  \u2713 Cache directory cleaned up successfully
Removed 12 cache entries from: /Users/user/.c4/cache
`

const DAEMON_CONTROL = `> daemon start

Starting daemon session in detached mode...
  \u2713 tmux available
  \u2713 Claude Code available
  \u2713 9 c4-daemon hooks validated
  \u2713 Internet connection available
  \u2713 Backend healthy
  \u2713 Message broker connected
  \u2713 Sessions verified: 2 running, 1 resumable
  \u2713 Daemon started successfully

> daemon stop
Clean up cache directory? [y/N] (default: no)
Press Enter for default, or type y/yes or n/no
>
  \u2192 Skipping cache cleanup
  \u2713 Daemon session stopped

> daemon restart
  \u2713 Daemon session stopped

Starting daemon session in detached mode...
  \u2713 tmux available
  \u2713 Claude Code available
  \u2713 9 c4-daemon hooks validated
  \u2713 Internet connection available
  \u2713 Backend healthy
  \u2713 Message broker connected
  \u2713 Sessions verified: 2 running, 1 resumable
  \u2713 Daemon started successfully
`

const SESSION_LIST = `> session list

Running Sessions:
  1. My Project (detached)
     ~/projects/my-project
  2. API Server (attached)
     ~/projects/api-server

Resumable Sessions:
  1. Feature Auth
     ~/projects/feature-auth

System Sessions:
  \u2022 Daemon Session (detached)
`

const SESSION_START_SEARCH = `> session start

Start Interactive Claude Code Session:
(Ctrl+C to go back at any step)

Select project:
  1. Search by keyword
  2. Browse known projects
  3. Enter path manually
  4. Use current directory [/Users/me]
  > 1

Enter search keyword:
  > my-app

Code Repositories:
  1. projects/my-app

Enter number to select (or 'b' to go back):
  > 1

Session name
  > Feature Auth
  \u2192 Will create session: Feature Auth

Enable bypass mode? [y/N] (default: no)
  > n
Initial message [Hello]
(press Enter to use default)
  > implement login flow
  \u2192 Initial message: implement login flow

  \u2713 Interactive Claude Code session 'Feature Auth' created in detached mode
  \u2713 Initial message sent to session

Attach to this session now? [Y/n] (default: yes)
  > y`

const SESSION_START_BROWSE = `> session start

Start Interactive Claude Code Session:
(Ctrl+C to go back at any step)

Select project:
  1. Search by keyword
  2. Browse known projects
  3. Enter path manually
  4. Use current directory [/Users/me]
  > 2

Code Repositories:
projects
   1. my-app
   2. api-server

Projects:
work
   3. design-docs

Enter number to select (or 'b' to go back):
  > 1

Session name
  > Bug Fix
  ...`

const SESSION_START_MANUAL = `> session start

Start Interactive Claude Code Session:
(Ctrl+C to go back at any step)

Select project:
  1. Search by keyword
  2. Browse known projects
  3. Enter path manually
  4. Use current directory [/Users/me]
  > 3
Working directory [/Users/me]
(press Enter to use default)
  > /Users/me/projects/new-app

Unknown code repository. Save for quick access? [Y/n] (default: yes)
  > y
  \u2713 Saved 'new-app' as code repository

Session name
  > Explore
  ...`

const SESSION_START_CURRENT = `> session start

Start Interactive Claude Code Session:
(Ctrl+C to go back at any step)

Select project:
  1. Search by keyword
  2. Browse known projects
  3. Enter path manually
  4. Use current directory [/Users/me]
  > 4
Use current directory? [Y/n] (default: yes)
  > y

  \u2192 /Users/me

Session name
  > Quick Task
  ...`

const SESSION_END = `> session end

  1. My Project
     ~/projects/my-project
  2. API Server
     ~/projects/api-server

Select session(s) to end (1-2)
Examples: 1 | 1,2,3
(Ctrl+C to cancel)
  > 1

  \u2713 Session 'My Project' ending gracefully
`

const SESSION_END_MULTI = `> session end

  1. Feature Auth
     ~/projects/feature-auth
  2. API Server
     ~/projects/api-server
  3. Bug Fix
     ~/projects/bug-fix

Select session(s) to end (1-3)
Examples: 1 | 1,2,3
(Ctrl+C to cancel)
  > 1,3

  \u2713 Session 'Feature Auth' ending gracefully
  \u2713 Session 'Bug Fix' ending gracefully
`

const SESSION_ATTACH = `> session attach

Available detached Claude Code sessions:
  1. My Project
     ~/projects/my-project
  2. API Server
     ~/projects/api-server

Select session to attach (1-2)
(Ctrl+C to cancel)
  > 1
  \u2192 Attaching to session 'My Project'...`

const SESSION_DETACH = `> session detach

Available attached Claude Code sessions:
  1. My Project
     ~/projects/my-project

Select session(s) to detach from (1-1)
Examples: 1 | 1,2,3
(Ctrl+C to cancel)
  > 1

  \u2192 Detaching from session 'My Project'...
  \u2713 Successfully detached from session 'My Project'
`

const SESSION_RENAME = `> session rename

Available Claude Code sessions:
  1. My Project
     ~/projects/my-project

Select session to rename (1-1)
(Ctrl+C to cancel)
  > 1

Enter new name for session 'My Project': (e.g., 'My New Project')
(Ctrl+C to go back)
  > Updated Project

  \u2713 Session renamed: My Project -> Updated Project
`

const SESSION_RESUME = `> session resume

Resume a Previous Claude Code Session:

  1. Api Server
     ~/projects/api-server
  2. Feature Auth
     ~/projects/feature-auth

Select session to resume (1-2)
(Ctrl+C to cancel)
  > 1

Initial message [default]
(press Enter for default, or type a custom message)
  > Lets continue where we left off

  \u2192 Initial message: Lets continue where we left off

  \u2192 Resuming session 'Api Server'...
  \u2713 Claude Code session 'Api Server' resumed in detached mode
  \u2192 Waiting for Claude Code to start...
  \u2713 Initial message sent to session

Attach to this session? [Y/n] (default: yes)
Press Enter for default, or type y/yes or n/no
  > n

  You can attach later with: session attach
`

const HOOKS_TOGGLE = `> hooks enable
  \u2713 Hooks enabled

> hooks disable
  \u2192 Hooks disabled
    (Backend will not receive hook events)
`

const ACTIVITY_TOGGLE = `> activity enable
  \u2713 Activity events enabled

> activity disable
  \u2192 Activity events disabled
    (Backend will not receive activity events)
`

const PERSISTENCE_TOGGLE = `> persistence enable
  \u2713 Event persistence enabled

> persistence disable
  \u2192 Event persistence disabled
    (Events will not be stored in database)
`

const ENCRYPTION_STATUS = `> encryption status

Encryption:
  Key ring:            configured
  User email:          user@example.com
  Total keys:          2
  Active version:      v2
  Active key:          ek_abcd...wxyz
  Updated at:          2025-02-14T10:30:45Z

  Key versions:
    v1   created 2025-02-10T00:00:00Z
    v2   created 2025-02-14T10:30:45Z
`

const ENCRYPTION_IMPORT = `> encryption import

Key Ring Import:
Import your encryption key ring exported from the C4 Dashboard
Provide the absolute path to the key ring JSON file

Enter absolute path to key ring file:
  > /Users/me/Downloads/key-ring.json

  \u2713 Key ring saved successfully
`

const ENCRYPTION_VERIFY = `> encryption verify

  \u2713 Key ring structure valid
  \u2713 v1: key format valid
  \u2713 v2: key format valid
  \u2713 File permissions: 600 (correct)

  \u2713 All 2 keys verified successfully
`

const LOGS_STATUS = `> logs status

Logs Status

Configuration:
  Directory: ~/.c4-daemon/logs
  Log level: info

Statistics:
  Total size: 2.3 MB
  Total files: 45
  Date range: 2025-02-10 to 2025-02-14

Breakdown:
  Daemon logs: 28 files (1.8 MB)
  Hooks logs: 17 files (0.5 MB)
`

const LOGS_LEVEL = `> logs level

Select Log Level

  1. trace (Most verbose - all details)
  2. debug (Verbose developer information)
  3. info \u2190 current (Important events (recommended))
  4. warn (Warning conditions only)
  5. error (Error conditions only)
  6. off (No logging)

Enter choice (1-6): 2

  \u2713 Configuration updated successfully
  Log level: info \u2192 debug

  \u2192 Restarting daemon to apply new configuration...

  \u2713 Daemon restarted with updated log level
`

const LOGS_CLEANUP = `> logs cleanup

Logs Cleanup

Cleanup Options:
1. All logs
2. Logs older than N days
3. Cancel

> 1

  ! This will delete:
  - 45 files (2.3 MB)

Delete 45 files (2.3 MB)? This cannot be undone. [y/N] (default: no)
Press Enter for default, or type y/yes or n/no
> yes

  \u2713 Deleted 45 files (2.3 MB)
`

const DISCOVERY_INIT = `> discovery init

Projects Discovery
\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500
Scan root directory [~]
(press Enter to use default)
>

Scanning for Git repositories in: ~
This may take a while on the first run...

  \u2713 Found 42 code repositories in 3.45s

Code Repositories (42)
\u251C\u2500 projects
\u2502  \u251C\u2500 my-app
\u2502  \u2514\u2500 api-server
\u2514\u2500 work
   \u2514\u2500 client-project

  \u2192 Cache updated.
Use 'start session' to select a project.
`

const DISCOVERY_UPDATE = `> discovery update

Projects Discovery Update
\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500
Scanning for changes in: ~

Scan completed in 1.23s

Removed Repositories (2)
These paths no longer exist on disk:

  1. projects/old-project
      ~/projects/old-project
  2. work/archived
      ~/work/archived

Remove these entries from cache? [Y/n] (default: yes)
Press Enter for default, or type y/yes or n/no
> y

New Repositories (3)
Found on disk but not in cache:

  1. projects/new-app
  2. experiments/proto
  3. work/latest

Select repositories to add:
  - Enter numbers like: 1,2,3 or 1 2 3
  - Type 'all' or 'a' to add everything
  - Type 'none' or 'n' to skip
  - Press Ctrl+C to cancel

> all

Update Summary
  \u2192 Removed 2 entries
  \u2713 Added 3 repositories

  \u2713 Cache updated successfully.
`

const DISCOVERY_DESCRIBE = `> discovery describe


Projects Discovery Status
\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500
Code Repositories: 3  |  Projects: 0
Last scan: 5m ago


Code Repositories (3)
\u251C\u2500 projects
\u2502  \u251C\u2500 my-app
\u2502  \u2514\u2500 api-server
\u2514\u2500 work
   \u2514\u2500 client-project
`

const commandLabelSx: SxProps<Theme> = {
  mt: 2,
  mb: 0.5,
  fontSize: '0.875rem',
  lineHeight: 1.8,
  scrollMarginTop: 80,
  '& code': {
    fontFamily: 'monospace',
    fontSize: '1.05em',
    color: (theme) =>
      theme.palette.mode === 'dark' ? theme.palette.error.light : theme.palette.error.main,
  },
}

const terminalBlockSx: SxProps<Theme> = {
  p: 2,
  my: 1.5,
  fontFamily: 'monospace',
  fontSize: '0.875rem',
  borderRadius: 1,
  overflow: 'auto',
  whiteSpace: 'pre',
  lineHeight: 1.6,
  backgroundColor: '#1e1e1e',
  color: '#d4d4d4',
}

const tocContainerSx: SxProps<Theme> = {
  p: 2.5,
  mb: 3,
  borderColor: 'divider',
  backgroundColor: 'action.hover',
  borderRadius: 1,
}

const tocHeadingSx: SxProps<Theme> = {
  fontWeight: 600,
  mb: 1.5,
  color: 'text.primary',
}

const tocNavSx: SxProps<Theme> = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
  columnGap: 3,
  rowGap: 1.5,
}

const tocSectionSx: SxProps<Theme> = {
  mb: 0,
}

const tocSectionLinkSx: SxProps<Theme> = {
  fontWeight: 600,
  fontSize: '0.9rem',
  color: 'text.primary',
  textDecoration: 'none',
  '&:hover': {
    textDecoration: 'underline',
    color: 'primary.main',
  },
  '&:focus-visible': {
    outline: '2px solid',
    outlineColor: 'primary.main',
    outlineOffset: '2px',
    borderRadius: '2px',
  },
}

const tocCommandListSx: SxProps<Theme> = {
  m: 0,
  pl: 2.5,
  listStyle: 'none',
  mt: 0.5,
}

const tocCommandItemSx: SxProps<Theme> = {
  fontSize: '0.8125rem',
  lineHeight: 1.7,
}

const tocCommandLinkSx: SxProps<Theme> = {
  textDecoration: 'none',
  '& code': {
    fontFamily: 'monospace',
    fontSize: '0.8125rem',
    color: (theme) =>
      theme.palette.mode === 'dark' ? theme.palette.error.light : theme.palette.error.main,
  },
  '&:hover code': {
    textDecoration: 'underline',
  },
  '&:focus-visible': {
    outline: '2px solid',
    outlineColor: 'primary.main',
    outlineOffset: '2px',
    borderRadius: '2px',
  },
}

const warningCalloutSx: SxProps<Theme> = {
  display: 'flex',
  gap: 1.5,
  mt: 2,
  p: 2,
  fontSize: '0.875rem',
  borderLeft: '3px solid',
  borderColor: 'warning.main',
  backgroundColor: 'action.hover',
  borderRadius: '0 4px 4px 0',
}

const warningCalloutIconSx: SxProps<Theme> = {
  fontSize: 20,
  color: 'warning.main',
  mt: 0.25,
  flexShrink: 0,
}

const infoCalloutSx: SxProps<Theme> = {
  display: 'flex',
  gap: 1.5,
  mt: 2,
  p: 2,
  fontSize: '0.875rem',
  borderLeft: '3px solid',
  borderColor: 'info.main',
  backgroundColor: 'action.hover',
  borderRadius: '0 4px 4px 0',
}

const infoCalloutIconSx: SxProps<Theme> = {
  fontSize: 20,
  color: 'info.main',
  mt: 0.25,
  flexShrink: 0,
}

const calloutTextSx: SxProps<Theme> = {
  '& code': {
    fontFamily: 'monospace',
    fontSize: '1.05em',
    color: (theme) =>
      theme.palette.mode === 'dark' ? theme.palette.error.light : theme.palette.error.main,
  },
}

const calloutListSx: SxProps<Theme> = {
  mt: 1.5,
  mb: 0,
  pl: 2.5,
  fontSize: '0.875rem',
  color: 'text.secondary',
  '& li': {
    mb: 0.5,
  },
  '& li:last-child': {
    mb: 0,
  },
  '& code': {
    fontFamily: 'monospace',
    fontSize: '1.05em',
    color: (theme) =>
      theme.palette.mode === 'dark' ? theme.palette.error.light : theme.palette.error.main,
  },
}

const backToTopSx: SxProps<Theme> = {
  position: 'fixed',
  bottom: 24,
  right: 24,
  backgroundColor: 'primary.main',
  color: 'primary.contrastText',
  '&:hover': {
    backgroundColor: 'primary.dark',
  },
}

const modeSubtitleSx: SxProps<Theme> = {
  fontWeight: 600,
  mt: 3,
  mb: 0.5,
  color: 'text.primary',
  scrollMarginTop: 80,
}

const comparisonTableSx: SxProps<Theme> = {
  mt: 3,
  mb: 1,
  borderRadius: 1,
  overflow: 'hidden',
  border: 1,
  borderColor: 'divider',
}

const comparisonHeaderSx: SxProps<Theme> = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr 1fr',
  backgroundColor: 'action.hover',
  borderBottom: 1,
  borderColor: 'divider',
}

const comparisonRowSx: SxProps<Theme> = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr 1fr',
  borderBottom: 1,
  borderColor: 'divider',
  '&:last-child': { borderBottom: 0 },
}

const comparisonHeadCellSx: SxProps<Theme> = {
  px: 2,
  py: 1,
  fontSize: '0.8125rem',
  fontWeight: 600,
  color: 'text.primary',
}

const comparisonLabelCellSx: SxProps<Theme> = {
  px: 2,
  py: 1,
  fontSize: '0.8125rem',
  fontWeight: 500,
  color: 'text.primary',
}

const comparisonCellSx: SxProps<Theme> = {
  px: 2,
  py: 1,
  fontSize: '0.8125rem',
  color: 'text.secondary',
}
